import React from 'react';
import { Box, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import MovieIcon from '@mui/icons-material/Movie';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { useDashboardStats } from './useDashboardStats';
import { hexWithAlpha } from './labCapabilities';

/**
 * Headline counts for what the user has actually made. Sits directly under the
 * hero so "assets created" is the first concrete thing on the page.
 *
 * Renders every tile including zeroes — a new account seeing "0 images" is a
 * clearer invitation than an empty strip, and it keeps the layout stable as
 * the counts load in.
 */
export const StatStrip: React.FC = () => {
  const stats = useDashboardStats();

  const tiles = [
    { label: 'Assets created', value: stats.total, icon: <AutoAwesomeIcon />, emphasis: true },
    { label: 'Images', value: stats.images, icon: <ImageIcon /> },
    { label: 'Documents', value: stats.documents, icon: <DescriptionIcon /> },
    { label: 'Videos', value: stats.videos, icon: <MovieIcon /> },
  ];

  return (
    <Stack
      direction="row"
      spacing={{ xs: 1.5, md: 2 }}
      flexWrap="wrap"
      useFlexGap
      aria-label="Your creative output"
    >
      {tiles.map(t => (
        <StatTile
          key={t.label}
          label={t.label}
          value={t.value}
          icon={t.icon}
          emphasis={t.emphasis}
          loading={stats.isLoading}
        />
      ))}
    </Stack>
  );
};

interface StatTileProps {
  label: string;
  value: number;
  icon: React.ReactElement;
  emphasis?: boolean;
  loading?: boolean;
}

const StatTile: React.FC<StatTileProps> = ({ label, value, icon, emphasis, loading }) => {
  const theme = useTheme();
  const accent = emphasis ? theme.palette.primary.main : theme.palette.text.secondary;

  return (
    <Box
      sx={{
        flex: '1 1 160px',
        minWidth: 150,
        borderRadius: 2,
        border: '1px solid',
        borderColor: emphasis ? hexWithAlpha(theme.palette.primary.main, 0.4) : 'divider',
        bgcolor: emphasis ? hexWithAlpha(theme.palette.primary.main, 0.07) : 'background.paper',
        px: 2,
        py: 1.5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ color: accent, mb: 0.5 }}>
        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
        <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>
          {label.toUpperCase()}
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="text" width={56} height={36} />
      ) : (
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{ lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}
        >
          {value.toLocaleString()}
        </Typography>
      )}
    </Box>
  );
};
